<template>
    <SmithsChartCard :has-data="hasData" :loading="loading" title="Bag Jams vs Throughput">
        <div class="bagjams-layout">
            <div class="bagjams-layout__chart">
                <c-chart :key="componentKey" :custom-tooltips="false" :data="chartDataset" :options="chartOptions" class="h-full" type="bar" />
            </div>
            <div class="bagjams-layout__right">
                <div v-if="hasDoughnutData" class="doughnut-wrap">
                    <c-chart :custom-tooltips="false" :data="doughnutDataset" :options="doughnutOptions" type="doughnut" />
                    <div class="doughnut-legend">
                        <div class="doughnut-legend__item">
                            <span class="dot dot--red" />
                            Downtime {{ formatTime(totalDowntimeMinutes) }}
                        </div>
                        <div class="doughnut-legend__item">
                            <span class="dot dot--blue" />
                            Bag Jams {{ formatTime(totalBagJamMinutes) }}
                        </div>
                    </div>
                </div>
                <SidebarKpi label="Bag Jams" :value="totalBagJams" />
            </div>
        </div>
    </SmithsChartCard>
</template>

<script setup>
import { computed, ref } from 'vue';
import { CChart } from '@coreui/vue-chartjs';
import SmithsChartCard from './SmithsChartCard.vue';
import SidebarKpi from './SidebarKpi.vue';
import { getBaseChartOptions, chartColors, lightTooltip } from '@/Utils/chartDefaults';

const props = defineProps({
    data: { type: Object, default: null },
    downtimeData: { type: Object, default: null },
    loading: { type: Boolean, default: false },
});

const componentKey = ref(0);
const hasData = computed(() => props.data?.labels?.length > 0);
const totalBagJams = computed(() => props.data?.bagJams?.reduce((a, b) => a + b, 0) ?? 0);

const totalDowntimeMinutes = computed(() => (props.downtimeData?.included ?? []).reduce((a, b) => a + b, 0));
const totalBagJamMinutes = computed(() => (props.downtimeData?.bagJam ?? []).reduce((a, b) => a + b, 0));
const hasDoughnutData = computed(() => totalDowntimeMinutes.value > 0 || totalBagJamMinutes.value > 0);

function formatTime(mins) {
    if (!mins) return '00:00:00';
    const totalSeconds = Math.round(mins * 60);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const doughnutDataset = computed(() => ({
    labels: ['Downtime', 'Bag Jams'],
    datasets: [
        {
            data: [totalDowntimeMinutes.value, totalBagJamMinutes.value],
            backgroundColor: [chartColors.red, chartColors.primaryLight],
            borderWidth: 0,
            cutout: '65%',
        },
    ],
}));

const doughnutOptions = computed(() => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
        legend: { display: false },
        tooltip: {
            enabled: true,
            callbacks: {
                label: (ctx) => `${ctx.label}: ${formatTime(ctx.parsed)}`,
            },
        },
    },
}));

const chartDataset = computed(() => {
    if (!hasData.value) return { labels: [], datasets: [] };

    return {
        labels: props.data.labels,
        datasets: [
            {
                type: 'bar',
                label: 'Bag Jams',
                data: props.data.bagJams,
                backgroundColor: chartColors.primaryFill,
                borderColor: chartColors.primary,
                borderWidth: 1,
                borderRadius: 3,
                barPercentage: 0.6,
                yAxisID: 'y',
                order: 2,
            },
            {
                type: 'line',
                label: 'Throughput',
                data: props.data.throughput,
                borderColor: chartColors.green,
                borderWidth: 2,
                tension: 0.3,
                pointRadius: 0,
                yAxisID: 'y1',
                order: 1,
            },
        ],
    };
});

const chartOptions = computed(() => ({
    ...getBaseChartOptions(),
    interaction: { mode: 'index', intersect: false },
    scales: {
        x: {
            grid: { display: false },
            ticks: { font: { size: 10 }, maxRotation: 45 },
        },
        y: {
            type: 'linear',
            position: 'left',
            beginAtZero: true,
            title: { display: true, text: 'Bag Jams', font: { size: 11 } },
            grid: { drawBorder: false },
        },
        y1: {
            type: 'linear',
            position: 'right',
            beginAtZero: true,
            title: { display: true, text: 'Throughput', font: { size: 11 } },
            grid: { display: false },
        },
    },
    plugins: {
        ...getBaseChartOptions().plugins,
        tooltip: { ...lightTooltip, enabled: true, mode: 'index', intersect: false },
    },
}));
</script>

<style scoped>
.bagjams-layout {
    display: flex;
    height: 100%;
}

.bagjams-layout__chart {
    flex: 1;
    min-width: 0;
}

.bagjams-layout__right {
    flex-shrink: 0;
    width: 180px;
    padding-left: 1rem;
    border-left: 1px solid var(--cui-card-border-color, #d8dbe0);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
}

.doughnut-wrap {
    width: 140px;
}

.doughnut-legend {
    margin-top: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.doughnut-legend__item {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.65rem;
    color: var(--cui-text-muted);
}

.dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.dot--red {
    background: #ebb6b7;
}

.dot--blue {
    background: #5577f5;
}

@media (max-width: 768px) {
    .bagjams-layout {
        flex-direction: column;
    }

    .bagjams-layout__right {
        width: 100%;
        flex-direction: row;
        padding-left: 0;
        padding-top: 1rem;
        border-left: none;
        border-top: 1px solid var(--cui-card-border-color, #d8dbe0);
    }
}
</style>
